/**
 * The setup sheet for one instrument: numbered steps to enter on the instrument, with the exact values highlighted.
 * Print renders a plain black-on-white copy of just the sheet.
 */
import { Fragment, ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Alert, Box, Button, GlobalStyles, Stack, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { Printer, Scroll } from "@phosphor-icons/react";
import { http, isNotFound, SetupSheet as Sheet } from "../api";
import { useSession } from "../AuthToken";
import { Mono } from "../ui";
import { fonts, tones } from "../theme";
import { CopyValue, NotYet, Panel } from "./common";

const PRINT_CLASS = "jupiter-printing";

/** Wrap occurrences of the sheet's values in mono so they stand out in step text. */
function highlight(text: string, values: string[], mono: (s: string, i: number) => ReactNode): ReactNode {
  const vals = values.filter((v) => v && v.length >= 2).sort((a, b) => b.length - a.length);
  if (!vals.length) return text;
  const re = new RegExp(`(${vals.map((v) => v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "g");
  return text.split(re).map((part, i) => (vals.includes(part) ? mono(part, i) : <Fragment key={i}>{part}</Fragment>));
}

export function SetupSheet({ nodeId, refreshKey }: { nodeId: string; refreshKey?: unknown }) {
  const { token } = useSession();
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing" | "error">("loading");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    let stop = false;
    (async () => {
      const r = await http.get<Sheet>(token, `/admin/nodes/${nodeId}/setup-sheet`);
      if (stop) return;
      if (r.ok) {
        setSheet(r.result);
        setState("ready");
      } else {
        setState(isNotFound(r.error) ? "missing" : "error");
        setError(r.error);
      }
    })();
    return () => {
      stop = true;
    };
  }, [token, nodeId, refreshKey]);

  useEffect(() => {
    const done = () => document.body.classList.remove(PRINT_CLASS);
    window.addEventListener("afterprint", done);
    return () => {
      window.removeEventListener("afterprint", done);
      done();
    };
  }, []);

  const print = () => {
    document.body.classList.add(PRINT_CLASS);
    window.print();
  };

  const values = sheet ? Object.values(sheet.values) : [];
  let n = 0;
  const numbered = sheet?.sections.map((s) => ({ ...s, steps: s.steps.map((t) => ({ n: ++n, t })) })) ?? [];

  return (
    <Panel
      title="Setup sheet"
      icon={<Scroll size={16} />}
      action={sheet && <Button size="small" variant="outlined" startIcon={<Printer size={14} />} onClick={print}>Print</Button>}
    >
      {state === "missing" && <NotYet>The setup sheet is not available on this server yet.</NotYet>}
      {state === "error" && <Alert severity="error">{error}</Alert>}
      {sheet && (
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h6">{sheet.title}</Typography>
            {sheet.intro && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 820 }}>{sheet.intro}</Typography>}
          </Box>
          {Object.keys(sheet.values).length > 0 && (
            <Box sx={{ border: 1, borderColor: "divider", borderRadius: "12px", overflowX: "auto" }}>
              <Table size="small">
                <TableBody>
                  {Object.entries(sheet.values).map(([k, v]) => (
                    <TableRow key={k}>
                      <TableCell sx={{ color: "text.secondary", width: { sm: 260 } }}>{k}</TableCell>
                      <TableCell><CopyValue value={v} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          {numbered.map((s) => (
            <Box key={s.heading}>
              <Typography variant="overline" sx={{ color: "primary.main", display: "block", lineHeight: 1.6, mb: 1 }}>{s.heading}</Typography>
              <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
                {s.steps.map((st) => (
                  <Box component="li" key={st.n} sx={{ display: "flex", gap: 1.5, mb: 1.25 }}>
                    <Box sx={{ flexShrink: 0, width: 24, height: 24, borderRadius: "50%", border: 1, borderColor: "divider", display: "grid", placeItems: "center", fontSize: 11.5, color: "primary.light", fontFamily: fonts.mono }}>{st.n}</Box>
                    <Typography variant="body2" sx={{ pt: "3px", wordBreak: "break-word" }}>
                      {highlight(st.t, values, (p, i) => <Mono key={i} sx={{ color: "primary.light" }}>{p}</Mono>)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
          {createPortal(<PrintCopy sheet={sheet} numbered={numbered} values={values} />, document.body)}
          <GlobalStyles
            styles={{
              ".jupiter-print-only": { display: "none" },
              "@media print": {
                [`body.${PRINT_CLASS}`]: { background: "#fff !important" },
                [`body.${PRINT_CLASS} > *:not(.jupiter-print-only)`]: { display: "none !important" },
                [`body.${PRINT_CLASS} > .jupiter-print-only`]: { display: "block" },
              },
            }}
          />
        </Stack>
      )}
    </Panel>
  );
}

function PrintCopy({ sheet, numbered, values }: { sheet: Sheet; numbered: { heading: string; steps: { n: number; t: string }[] }[]; values: string[] }) {
  const mono = (p: string, i: number) => <code key={i} style={{ fontFamily: fonts.mono, fontWeight: 600 }}>{p}</code>;
  return (
    <div className="jupiter-print-only" style={{ color: "#000", background: "#fff", fontFamily: "Helvetica, Arial, sans-serif", fontSize: 12, padding: 8 }}>
      <div style={{ fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", color: "#555" }}>Jupiter by Moyae · setup sheet</div>
      <h1 style={{ fontSize: 20, margin: "4px 0 6px", fontWeight: 500 }}>{sheet.title}</h1>
      {sheet.intro && <p style={{ margin: "0 0 12px", color: "#333" }}>{sheet.intro}</p>}
      {Object.keys(sheet.values).length > 0 && (
        <table style={{ borderCollapse: "collapse", marginBottom: 14, width: "100%" }}>
          <tbody>
            {Object.entries(sheet.values).map(([k, v]) => (
              <tr key={k}>
                <td style={{ border: "1px solid #bbb", padding: "4px 8px", width: "40%" }}>{k}</td>
                <td style={{ border: "1px solid #bbb", padding: "4px 8px", fontFamily: fonts.mono, fontWeight: 600 }}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {numbered.map((s) => (
        <div key={s.heading} style={{ marginBottom: 12, breakInside: "avoid" }}>
          <h2 style={{ fontSize: 13, margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "0.12em" }}>{s.heading}</h2>
          {s.steps.map((st) => (
            <div key={st.n} style={{ display: "flex", gap: 8, marginBottom: 5 }}>
              <span style={{ width: 20, flexShrink: 0, fontWeight: 600 }}>{st.n}.</span>
              <span>{highlight(st.t, values, mono)}</span>
              <span style={{ marginLeft: "auto", paddingLeft: 12, color: "#999" }}>&#9744;</span>
            </div>
          ))}
        </div>
      ))}
      <div style={{ marginTop: 18, fontSize: 9, color: tones.ink }}>Printed {new Date().toLocaleString()}</div>
    </div>
  );
}
